'use client';

/**
 * Which social sign-in providers this environment has configured (BL-2776).
 *
 * A pre-auth REST read of `GET /api/auth/social-providers`, made once on mount.
 * `providers` is empty until it settles and after a failure, so a button the
 * server did not list is never shown; email + password sign-in never waits on it.
 */

import { useEffect, useState } from 'react';

import type { SocialProviderId } from '@auctogon/core-admin/auth/social-providers';

import { api } from '../api-client/typed-client';

/** The hook's own message when the read fails without a server sentence. */
const PROVIDERS_LOAD_FAILED = 'Could not load the sign-in options.';

/** What {@link useSocialProviders} returns. */
export interface UseSocialProvidersResult {
    /** The configured providers; empty while loading or after an error. */
    providers: SocialProviderId[];
    loading: boolean;
    error: string | null;
}

/**
 * Read the configured social providers once.
 *
 * @returns the providers plus load and error state.
 * @sideEffect GETs `/api/auth/social-providers` on mount.
 */
export function useSocialProviders(): UseSocialProvidersResult {
    const [providers, setProviders] = useState<SocialProviderId[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        void (async () => {
            // A rejected fetch lands on `error` like a non-2xx does (error-handling-chain.md).
            try {
                const res = await api.auth.socialProviders.get();
                if (cancelled) return;
                if (!res.success) {
                    setError(res.error ?? PROVIDERS_LOAD_FAILED);
                } else {
                    setProviders(res.data.providers);
                    setError(null);
                }
            } catch (err) {
                if (cancelled) return;
                setError(err instanceof Error && err.message ? err.message : PROVIDERS_LOAD_FAILED);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    return { providers, loading, error };
}
